import {
  parseAdminGuardianLink,
  parseAdminGuardianLinksResponse,
  parseAdminGuardianInvite,
  parseAdminGuardianInvitesResponse,
  parseApproveGuardianLinkRequestRequest,
  parseCreateAdminGuardianInviteRequest,
  parseCreateAdminGuardianInviteResponse,
  parseConsumeGuardianInviteResponse,
  parseGuardianLinkRequest,
  parseGuardianLinkRequestListResponse,
  parseResolvedGuardianInvite
} from "@madamuh/domain";
import type {
  AdminGuardianLinkDto,
  AdminGuardianLinksResponseDto,
  AdminGuardianInviteDto,
  AdminGuardianInvitesResponseDto,
  ApproveGuardianLinkRequestRequestDto,
  CreateAdminGuardianInviteRequestDto,
  CreateAdminGuardianInviteResponseDto,
  ConsumeGuardianInviteRequestDto,
  ConsumeGuardianInviteResponseDto,
  CreateGuardianLinkRequestDto,
  GuardianLinkRequestDto,
  GuardianLinkRequestListResponseDto,
  ResolveGuardianInviteRequestDto,
  ResolvedGuardianInviteDto
} from "@madamuh/domain";
import { apiGet, apiPost } from "./client";

export async function resolveGuardianInvite(
  tenantSlug: string,
  request: ResolveGuardianInviteRequestDto
): Promise<ResolvedGuardianInviteDto> {
  const response = await apiPost<ResolveGuardianInviteRequestDto, unknown>(
    `/api/v1/app/tenants/${tenantSlug}/guardian-invites/resolve`,
    request
  );

  return parseResolvedGuardianInvite(response);
}

export async function consumeGuardianInvite(
  tenantSlug: string,
  request: ConsumeGuardianInviteRequestDto
): Promise<ConsumeGuardianInviteResponseDto> {
  const response = await apiPost<ConsumeGuardianInviteRequestDto, unknown>(
    `/api/v1/app/tenants/${tenantSlug}/guardian-links/consume-invite`,
    request
  );

  return parseConsumeGuardianInviteResponse(response);
}

export async function createGuardianLinkRequest(
  tenantSlug: string,
  request: CreateGuardianLinkRequestDto
): Promise<GuardianLinkRequestDto> {
  const response = await apiPost<CreateGuardianLinkRequestDto, unknown>(
    `/api/v1/app/tenants/${tenantSlug}/guardian-link-requests`,
    request
  );

  return parseGuardianLinkRequest(response);
}

export async function fetchAdminGuardianLinkRequests(
  tenantSlug: string
): Promise<GuardianLinkRequestListResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/guardian-link-requests`);
  return parseGuardianLinkRequestListResponse(response);
}

export async function fetchAdminGuardianInvites(
  tenantSlug: string
): Promise<AdminGuardianInvitesResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/guardian-invites`);
  return parseAdminGuardianInvitesResponse(response);
}

export async function fetchAdminGuardianLinksForStudent(
  tenantSlug: string,
  studentId: string
): Promise<AdminGuardianLinksResponseDto> {
  const response = await apiGet<unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/students/${studentId}/guardian-links`
  );
  return parseAdminGuardianLinksResponse(response);
}

export async function createAdminGuardianInvite(
  tenantSlug: string,
  request: CreateAdminGuardianInviteRequestDto
): Promise<CreateAdminGuardianInviteResponseDto> {
  const parsedRequest = parseCreateAdminGuardianInviteRequest(request);
  const response = await apiPost<CreateAdminGuardianInviteRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/guardian-invites`,
    parsedRequest
  );

  return parseCreateAdminGuardianInviteResponse(response);
}

export async function revokeAdminGuardianInvite(
  tenantSlug: string,
  inviteId: string
): Promise<AdminGuardianInviteDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/guardian-invites/${inviteId}/revoke`,
    {}
  );

  return parseAdminGuardianInvite(response);
}

export async function reissueAdminGuardianInvite(
  tenantSlug: string,
  inviteId: string
): Promise<CreateAdminGuardianInviteResponseDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/guardian-invites/${inviteId}/reissue`,
    {}
  );

  return parseCreateAdminGuardianInviteResponse(response);
}

export async function revokeAdminGuardianLink(
  tenantSlug: string,
  linkId: string
): Promise<AdminGuardianLinkDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/guardian-links/${linkId}/revoke`,
    {}
  );

  return parseAdminGuardianLink(response);
}

export async function approveGuardianLinkRequest(
  tenantSlug: string,
  requestId: string,
  request: ApproveGuardianLinkRequestRequestDto
): Promise<GuardianLinkRequestDto> {
  const parsedRequest = parseApproveGuardianLinkRequestRequest(request);
  const response = await apiPost<ApproveGuardianLinkRequestRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/guardian-link-requests/${requestId}/approve`,
    parsedRequest
  );

  return parseGuardianLinkRequest(response);
}

export async function rejectGuardianLinkRequest(
  tenantSlug: string,
  requestId: string
): Promise<GuardianLinkRequestDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/guardian-link-requests/${requestId}/reject`,
    {}
  );

  return parseGuardianLinkRequest(response);
}
